/**
 * Epic-grouped story tree with drag-drop reordering
 * Story 198: View and filter stories in backlog
 *
 * Uses @atlaskit/pragmatic-drag-and-drop for cross-epic story movement.
 */

import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { EpicNode, isEpicDragData } from './EpicNode';
import { StoryNode, isStoryDragData } from './StoryNode';
import { useReorderStory } from '../../hooks/useStoryMutations';
import { useReorderEpics } from '../../hooks/useEpicMutations';
import { useEpicExpandState } from '../../hooks/useEpicExpandState';
import type { StoryListItem } from '../../hooks/useStories';
import type { EpicListItem } from '../../hooks/useEpics';
import './StoryTree.css';

interface StoryTreeProps {
  stories: StoryListItem[];
  epics: EpicListItem[];
  projectSlug: string;
  selectedStoryId: string | null;
  onSelectStory: (storyExternalId: string) => void;
  onCreateStory?: () => void;
  onCreateEpic?: () => void;
}

export function StoryTree({
  stories,
  epics,
  projectSlug,
  selectedStoryId,
  onSelectStory,
  onCreateStory,
  onCreateEpic,
}: StoryTreeProps) {
  const reorderStory = useReorderStory();
  const reorderEpics = useReorderEpics();
  const epicExpandState = useEpicExpandState(projectSlug);
  const unassignedRef = useRef<HTMLDivElement>(null);
  const [isOverUnassigned, setIsOverUnassigned] = useState(false);

  // Group stories by epic
  const storiesByEpic = useMemo(() => {
    const groups = new Map<string | null, StoryListItem[]>();

    // Initialize with null for unassigned stories
    groups.set(null, []);

    // Initialize groups for each epic
    for (const epic of epics) {
      groups.set(epic.slug, []);
    }

    // Assign stories to groups
    for (const story of stories) {
      const epicSlug = story.epic_slug;
      if (!groups.has(epicSlug)) {
        groups.set(epicSlug, []);
      }
      groups.get(epicSlug)!.push(story);
    }

    // Sort stories within each group by priority
    for (const group of groups.values()) {
      group.sort((a, b) => a.priority - b.priority);
    }

    return groups;
  }, [stories, epics]);

  // Sort epics by order_index
  const sortedEpics = useMemo(
    () => [...epics].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)),
    [epics]
  );

  const unassignedStories = storiesByEpic.get(null) ?? [];

  // Monitor for story drops and handle reordering
  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => isStoryDragData(source.data),
      onDrop: ({ source, location }) => {
        const destination = location.current.dropTargets[0];
        if (!destination) return;

        const sourceData = source.data;
        if (!isStoryDragData(sourceData)) return;

        const destData = destination.data as { type: string; epicSlug?: string; storyId?: string };

        // Handle drop on another story (reorder within/across epics)
        if (destData.type === 'story' && destData.storyId) {
          const targetStoryId = destData.storyId;
          const targetEpic = destData.epicSlug ?? null;
          const edge = extractClosestEdge(destination.data);

          // Find target story's position
          const epicStories = storiesByEpic.get(targetEpic) ?? [];
          const targetIndex = epicStories.findIndex((s) => s.external_id === targetStoryId);

          if (targetIndex === -1) return;

          // Calculate priority based on edge
          let targetPriority: number;
          if (edge === 'top') {
            // Insert before target
            if (targetIndex === 0) {
              targetPriority = epicStories[0].priority - 1000;
            } else {
              const prevPriority = epicStories[targetIndex - 1].priority;
              const currPriority = epicStories[targetIndex].priority;
              targetPriority = Math.floor((prevPriority + currPriority) / 2);
            }
          } else {
            // Insert after target (bottom edge)
            if (targetIndex === epicStories.length - 1) {
              targetPriority = epicStories[targetIndex].priority + 1000;
            } else {
              const currPriority = epicStories[targetIndex].priority;
              const nextPriority = epicStories[targetIndex + 1].priority;
              targetPriority = Math.floor((currPriority + nextPriority) / 2);
            }
          }

          reorderStory.mutate({
            projectSlug,
            storyExternalId: sourceData.storyId,
            targetEpic,
            targetPriority,
          });
          return;
        }

        // Handle drop on epic container or unassigned
        if (destData.type !== 'epic' && destData.type !== 'unassigned') return;

        const targetEpic = destData.type === 'unassigned' ? null : (destData.epicSlug ?? null);
        const sourceEpic = sourceData.epicSlug;

        // Skip if dropping in same epic (without specific position)
        if (targetEpic === sourceEpic) return;

        // Calculate target priority (append at end)
        const epicStories = storiesByEpic.get(targetEpic) ?? [];
        const targetPriority =
          epicStories.length > 0
            ? epicStories[epicStories.length - 1].priority + 1000
            : 1000;

        reorderStory.mutate({
          projectSlug,
          storyExternalId: sourceData.storyId,
          targetEpic,
          targetPriority,
        });
      },
    });
  }, [projectSlug, storiesByEpic, reorderStory]);

  // Helper to perform epic reorder with View Transitions API
  const performEpicReorder = useCallback(
    (sourceSlug: string, targetSlug: string, edge: 'top' | 'bottom') => {
      const mutationParams = {
        projectSlug,
        activeSlug: sourceSlug,
        overSlug: targetSlug,
        edge,
      };

      // Use View Transitions API if available for smooth animation
      if (document.startViewTransition) {
        document.startViewTransition(() => {
          // flushSync ensures React updates DOM synchronously for view transition
          flushSync(() => {
            reorderEpics.mutate(mutationParams);
          });
        });
      } else {
        reorderEpics.mutate(mutationParams);
      }
    },
    [projectSlug, reorderEpics]
  );

  // Monitor for epic drops and handle reordering
  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => isEpicDragData(source.data),
      onDrop: ({ source, location }) => {
        const destination = location.current.dropTargets[0];
        if (!destination) return;

        const sourceData = source.data;
        if (!isEpicDragData(sourceData)) return;

        const destData = destination.data as { type: string; epicSlug?: string };

        // Only handle drop on another epic
        if (destData.type !== 'epic' || !destData.epicSlug) return;

        const sourceSlug = sourceData.epicSlug;
        const targetSlug = destData.epicSlug;

        if (sourceSlug === targetSlug) return;

        const edge = extractClosestEdge(destination.data);

        // Pass edge to backend so it can calculate proper position
        performEpicReorder(sourceSlug, targetSlug, edge === 'top' ? 'top' : 'bottom');
      },
    });
  }, [performEpicReorder]);

  // Make unassigned section a drop target
  useEffect(() => {
    const element = unassignedRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: () => ({ type: 'unassigned' }),
      canDrop: ({ source }) => isStoryDragData(source.data),
      onDragEnter: () => setIsOverUnassigned(true),
      onDragLeave: () => setIsOverUnassigned(false),
      onDrop: () => setIsOverUnassigned(false),
    });
  }, []);

  return (
    <div className="story-tree">
      {/* Epics with their stories */}
      {sortedEpics.map((epic) => (
        <EpicNode
          key={epic.id}
          epic={epic}
          stories={storiesByEpic.get(epic.slug) ?? []}
          projectSlug={projectSlug}
          selectedStoryId={selectedStoryId}
          onSelectStory={onSelectStory}
          isExpanded={epicExpandState.isExpanded(epic.slug)}
          onToggleExpanded={() => epicExpandState.toggleExpanded(epic.slug)}
        />
      ))}

      {/* Unassigned stories - collapsible drop target */}
      <div className="story-tree__unassigned">
        <button
          className="story-tree__unassigned-header"
          onClick={() => epicExpandState.toggleExpanded('__unassigned')}
          type="button"
        >
          <svg
            className={`story-tree__unassigned-chevron ${epicExpandState.isExpanded('__unassigned') ? 'story-tree__unassigned-chevron--expanded' : ''}`}
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06z" />
          </svg>
          <span>Unassigned</span>
        </button>
        {epicExpandState.isExpanded('__unassigned') && (
          <div
            ref={unassignedRef}
            className={`story-tree__unassigned-list ${isOverUnassigned ? 'story-tree__unassigned-list--drag-over' : ''}`}
          >
            {unassignedStories.length > 0 ? (
              unassignedStories.map((story) => (
                <StoryNode
                  key={story.id}
                  story={story}
                  projectSlug={projectSlug}
                  isSelected={story.external_id === selectedStoryId}
                  onSelect={() => onSelectStory(story.external_id)}
                  draggable
                />
              ))
            ) : (
              <div className="story-tree__empty-drop">Drop stories here</div>
            )}
          </div>
        )}
      </div>

      {sortedEpics.length === 0 && unassignedStories.length === 0 && (
        <div className="story-tree__empty">
          {onCreateEpic && (
            <button
              type="button"
              className="story-tree__create-button"
              onClick={onCreateEpic}
            >
              + Create New Epic
            </button>
          )}
          {onCreateStory && (
            <button
              type="button"
              className="story-tree__create-button"
              onClick={onCreateStory}
            >
              + Create New Story
            </button>
          )}
          {!onCreateEpic && !onCreateStory && 'No epics or stories found'}
        </div>
      )}
    </div>
  );
}
